import React from 'react';
import { useOrganizations } from '../../hooks/useOrganizations';
import { useDepartments } from '../../hooks/useDepartments';
import { useOrgUnitLevels } from '../../hooks/useOrgUnitLevels';
import { useRole } from '../../hooks/useRoles'; // Import useRole
import { StyledEngineProvider } from '@mui/material';

export default function RoleDetailsModal({ isOpen, onClose, role, title }) {
    // --- Data Fetching for Name Resolution ---
    // Fetch full details of the specific role to ensure we have all fields
    const { data: roleDetail, isLoading: roleLoading } = useRole(role?.id, role?.org);
    const activeRole = roleDetail || role;

    const { data: orgsData, isLoading: orgsLoading } = useOrganizations();
    const organizations = orgsData?.results || orgsData || [];

    const activeRoleOrgId = activeRole?.org_id || activeRole?.org?.id || activeRole?.org;
    const { data: deptData, isLoading: deptLoading } = useDepartments(activeRoleOrgId);
    const departments = deptData?.results || deptData || [];

    const { data: levelsData, isLoading: levelsLoading } = useOrgUnitLevels({ orgId: activeRole?.org }, { enabled: !!activeRole?.org });
    const levelsList = levelsData?.results || levelsData || [];

    if (!isOpen || !role) return null;

    // Helper to find names
    const getOrgName = (id) => organizations.find(o => String(o.id) === String(id))?.name || 'Loading...';

    const getDeptName = (idOrObj) => {
        if (!idOrObj) return '-';
        if (typeof idOrObj === 'object') return idOrObj.name || '-';
        return departments.find(d => String(d.id) === String(idOrObj))?.name || '-';
    };

    // Handle both ID and object for unit level
    const getLevelName = (level) => {
        if (!level) return '-';
        if (typeof level === 'object') return level.name || '-';
        return levelsList.find(l => l.id === level)?.name || '-';
    };

    // Display Values
    const orgId = activeRole.org_id || activeRole.org?.id || activeRole.org;
    const orgName = orgsLoading ? 'Loading...' : getOrgName(orgId);
    const deptValue = activeRole.department_id || activeRole.department;
    const deptName = deptLoading ? 'Loading...' : getDeptName(deptValue);

    // Check various potential keys for unit level
    const unitLevel = activeRole.org_unit_level_id || activeRole.org_unit_level;
    const levelName = levelsLoading ? 'Loading...' : getLevelName(unitLevel);

    return (
        <StyledEngineProvider injectFirst>
            <div className="fixed p-15 w-full max-w-[500px] left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[1001]">
                <div className='py-10 md:px-10 px-[7px] bg-bg-dark rounded-lg shadow-xl border border-border'>
                    <h5 className="text-[16px] font-medium py-10 mb-15 text-center border-b border-border">{title}</h5>

                    <div className="space-y-10 px-5">
                        {roleLoading ? (
                            <div className="text-center py-10 text-text-secondary">Loading role details...</div>
                        ) : (
                            <div className="flex flex-col gap-15">
                                {/* Organization */}
                                <div className='flex flex-col'>
                                    <label className='mb-1 text-xs font-medium text-text-main'>Organization</label>
                                    <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                        {orgName}
                                    </div>
                                </div>

                                {/* Department */}
                                <div className='flex flex-col'>
                                    <label className='mb-1 text-xs font-medium text-text-main'>Department</label>
                                    <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                        {deptName}
                                    </div>
                                </div>

                                <div className='flex justify-between items-center gap-4'>
                                    {/* Role Name */}
                                    <div className='flex flex-col w-full'>
                                        <label className='mb-1 text-xs font-medium text-text-main'>Role Name</label>
                                        <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                            {activeRole.name}
                                        </div>
                                        <span className='text-[10px] text-text-secondary mt-1'>
                                            Code: {activeRole.code}
                                        </span>
                                    </div>

                                    {/* Role Unit Level */}
                                    <div className='flex flex-col w-full'>
                                        <label className='mb-1 text-xs font-medium text-text-main'>Organisation Unit Level</label>
                                        <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                            {levelName}
                                        </div>
                                    </div>
                                </div>

                                {/* System Status */}
                                <div className='flex flex-col'>
                                    <label className='mb-1 text-xs font-medium text-text-main'>Is System Role?</label>
                                    <div className='bg-bg-light border border-border py-2 px-3 rounded-[4px] text-sm text-text-main'>
                                        {activeRole.is_system ? 'Yes' : 'No'}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className='flex gap-10 justify-end mt-15 pt-10 border-t border-border'>
                            <button
                                type='button'
                                onClick={onClose}
                                className='btn btn-outline-secondary btn-sm'
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            {/* Overlay */}
            <div onClick={onClose} className='fixed z-[1000] w-full h-full left-0 top-0 bg-black/50 backdrop-blur-[2px]'></div>
        </StyledEngineProvider>
    );
}
